import * as Sentry from '@sentry/react'

const BASE = (import.meta.env.VITE_API_BASE_URL || '') + '/api/v1'

// Routes that are already "public" auth surfaces — a 401 that fires
// while the user is on one of these doesn't need to redirect anywhere
// (they're already at the login screen) and attempting to would create
// a redirect loop for the POST /auth/login bad-credentials path.
const AUTH_PUBLIC_PATHS = new Set([
  '/', '/login', '/auth', '/welcome', '/privacy',
])

const SESSION_TOKEN_KEY = 'haven_token'

// Stamped once per SPA lifetime so we don't fire the redirect repeatedly
// if multiple in-flight requests 401 in the same tick.
let _redirectingForAuth = false

// ── Retry helper ──────────────────────────────────────────────────────────
// Bounded exponential backoff for transient failures. Triggered by:
//   • TypeError thrown from fetch() — true network failures (offline,
//     DNS, CORS preflight). At the OS layer these are deterministic; if
//     the link is back, the retry succeeds.
//   • 5xx HTTP responses — server-side transients (Render free-tier
//     cold start, brief Postgres pool exhaustion, Anthropic API hiccup).
//
// 4xx is NEVER retried — those are deterministic client signals
// (validation, auth, not found). Retrying would amplify load without
// changing the outcome.
//
// Final failure re-throws untouched so the existing Sentry capture in
// request() (TypeError catch + 5xx branch) fires exactly once at the
// end of the retry chain — no double-reporting, no swallowed errors.
// Exponential backoff: 1s, 2s, 4s — capped at 3 retries (4 total attempts).
// Tightened May 21 2026 after a pool-exhaustion incident: the previous
// 500/1000/2000 schedule meant a single user with a slow Supabase auth
// round-trip would re-fire requests fast enough to keep the backend
// pool pinned while it was already struggling to drain. Spacing retries
// out at least 1s gives the pool time to recover instead of compounding
// pressure with our own client.
const RETRY_BACKOFF_MS = [1000, 2000, 4000]

const _sleep = ms => new Promise(r => setTimeout(r, ms))

async function _fetchWithRetry(url, init, { method, path }) {
  for (let attempt = 0; attempt <= RETRY_BACKOFF_MS.length; attempt++) {
    const isLastAttempt = attempt === RETRY_BACKOFF_MS.length
    try {
      const res = await fetch(url, init)
      if (res.status >= 500 && !isLastAttempt) {
        console.warn(
          `[api] ${method} ${path} → ${res.status}, retry ${attempt + 1}/${RETRY_BACKOFF_MS.length} in ${RETRY_BACKOFF_MS[attempt]}ms`
        )
        await _sleep(RETRY_BACKOFF_MS[attempt])
        continue
      }
      return res
    } catch (err) {
      // TypeError is fetch's canonical "network unreachable" signal.
      // Other exceptions (AbortError, etc.) shouldn't retry.
      if (!(err instanceof TypeError) || isLastAttempt) {
        throw err
      }
      console.warn(
        `[api] ${method} ${path} network error, retry ${attempt + 1}/${RETRY_BACKOFF_MS.length} in ${RETRY_BACKOFF_MS[attempt]}ms`
      )
      await _sleep(RETRY_BACKOFF_MS[attempt])
    }
  }
  // Unreachable — loop body always returns or throws on the last attempt.
  throw new Error('_fetchWithRetry: exhausted without resolving')
}

function _handleStaleJwt() {
  if (_redirectingForAuth) return
  try { localStorage.removeItem(SESSION_TOKEN_KEY) } catch { /* private mode */ }
  const here = typeof window !== 'undefined' ? window.location.pathname : ''
  if (typeof window === 'undefined' || AUTH_PUBLIC_PATHS.has(here)) return
  _redirectingForAuth = true
  // Full navigation (not router push) so in-memory app state resets
  // cleanly — stale cached user objects, open WebSockets, etc.
  window.location.href = '/login'
}

async function request(method, path, body, token) {
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const startedAt = performance.now()
  let res
  try {
    res = await _fetchWithRetry(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }, { method, path })
  } catch (networkErr) {
    // Offline / CORS / DNS — never makes it to the server. Capture here so
    // the caller can still handle it, but Sentry sees it regardless.
    Sentry.addBreadcrumb({
      category: 'api',
      level: 'error',
      message: `${method} ${path} network error`,
      data: { method, path, duration_ms: Math.round(performance.now() - startedAt) },
    })
    Sentry.captureException(networkErr, {
      tags: { api_path: path, api_method: method, failure: 'network' },
    })
    throw Object.assign(networkErr, { status: 0, path, method })
  }

  // Leave a breadcrumb for every API call — lets the Sentry event timeline
  // show the exact sequence of requests leading up to an error.
  Sentry.addBreadcrumb({
    category: 'api',
    level: res.ok ? 'info' : 'error',
    message: `${method} ${path} → ${res.status}`,
    data: {
      method,
      path,
      status: res.status,
      duration_ms: Math.round(performance.now() - startedAt),
    },
  })

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ detail: res.statusText }))
    const apiError = Object.assign(new Error(errBody.detail || 'Request failed'), {
      status: res.status,
      path,
      method,
    })
    // Stale / expired JWT — clear session and bounce to /login so the
    // user gets the login screen instead of an in-screen error state.
    // Fires on 401 from ANY authenticated request (not just /auth/me);
    // guards above prevent loops when the caller is already on a
    // public auth surface (bad-credentials POST to /auth/login, etc.).
    if (res.status === 401) {
      _handleStaleJwt()
    }
    // Auto-capture 5xx so a missing catch elsewhere can't hide a server bug.
    // 4xx is intentionally left to the caller — validation / auth errors
    // are expected signals and would drown the event feed.
    if (res.status >= 500) {
      Sentry.captureException(apiError, {
        tags: { api_path: path, api_method: method, http_status: String(res.status) },
      })
    }
    throw apiError
  }

  return res.status === 204 ? null : res.json()
}

export const api = {
  get: (path, token) => request('GET', path, null, token),
  post: (path, body, token) => request('POST', path, body, token),
  put: (path, body, token) => request('PUT', path, body, token),
  patch: (path, body, token) => request('PATCH', path, body, token),
  // ``body`` is optional — most DELETEs use the path alone, but the
  // /push/unsubscribe endpoint needs a JSON body keyed on endpoint
  // (the same shape the browser handed us at subscribe time).
  delete: (path, token, body) => request('DELETE', path, body, token),
}

// Wake up the backend on app load (Render free tier cold start)
const HEALTH_URL = (import.meta.env.VITE_API_BASE_URL || '') + '/health'
export function warmupBackend() {
  fetch(HEALTH_URL, { method: 'GET' }).catch(() => {})
}
